import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import {
  Key, Plus, Eye, EyeOff, Pencil, Trash2, Lock, Shield, Loader2, X,
  AlertTriangle, Clock, CheckCircle, Activity, XCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const SERVICE_ICONS = {
  google_oauth: '🔑',
  stripe: '💳',
  razorpay: '💰',
  openai: '🤖',
  smtp: '📧',
  custom: '⚙️',
};

const AdminApiKeysPage = () => {
  const [keys, setKeys] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sensitive session
  const [sessionToken, setSessionToken] = useState('');
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [sessionRemaining, setSessionRemaining] = useState(0);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyPassword, setVerifyPassword] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Key form
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [keyForm, setKeyForm] = useState({ name: '', service: '', fields: {} });
  const [saving, setSaving] = useState(false);

  // Reveal state
  const [revealedKeys, setRevealedKeys] = useState({});
  const [revealing, setRevealing] = useState({});

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Key sources
  const [sources, setSources] = useState([]);

  // Health check
  const [healthResults, setHealthResults] = useState([]);
  const [healthChecking, setHealthChecking] = useState(false);
  const [healthCheckedAt, setHealthCheckedAt] = useState(null);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const [keysRes, sourcesRes] = await Promise.all([
        api.get('/admin/api-keys'),
        api.get('/admin/api-keys/sources'),
      ]);
      setKeys(keysRes.data.keys || []);
      setCategories(keysRes.data.categories || []);
      setSources(sourcesRes.data.sources || []);
    } catch { toast.error('Failed to load API keys'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  // Session countdown timer
  useEffect(() => {
    if (!sessionExpiry) { setSessionRemaining(0); return; }
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.floor((new Date(sessionExpiry) - new Date()) / 1000));
      setSessionRemaining(remaining);
      if (remaining <= 0) {
        setSessionToken('');
        setSessionExpiry(null);
        setRevealedKeys({});
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionExpiry]);

  const isSessionActive = sessionToken && sessionRemaining > 0;

  const requireSession = (action) => {
    if (isSessionActive) {
      action();
    } else {
      setPendingAction(() => action);
      setShowVerifyModal(true);
      setVerifyPassword('');
      setVerifyCode('');
    }
  };

  const handleVerifySession = async () => {
    if (!verifyPassword) {
      toast.error('Password is required');
      return;
    }
    setVerifying(true);
    try {
      const res = await api.post('/admin/api-keys/verify-session', {
        password: verifyPassword,
        code: verifyCode || '000000',
      });
      setSessionToken(res.data.session_token);
      setSessionExpiry(res.data.expires_at);
      setShowVerifyModal(false);
      toast.success('Session verified');
      if (pendingAction) {
        setTimeout(() => pendingAction(), 100);
        setPendingAction(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    } finally { setVerifying(false); }
  };

  const openAddModal = () => {
    setEditingKey(null);
    setKeyForm({ name: '', service: 'custom', fields: {} });
    setShowKeyModal(true);
  };

  const openEditModal = (key) => {
    requireSession(() => {
      setEditingKey(key);
      setKeyForm({
        name: key.name,
        service: key.service,
        fields: { ...key.fields },
      });
      setShowKeyModal(true);
    });
  };

  const handleSaveKey = async () => {
    if (!keyForm.name.trim()) {
      toast.error('Key name is required');
      return;
    }
    if (!isSessionActive) {
      requireSession(() => handleSaveKey());
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/api-keys', {
        key_id: editingKey?.id || null,
        name: keyForm.name,
        service: keyForm.service,
        fields: keyForm.fields,
        session_token: sessionToken,
      });
      toast.success(editingKey ? 'API key updated' : 'API key created');
      setShowKeyModal(false);
      fetchKeys();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to save';
      if (detail.includes('session')) {
        setSessionToken('');
        setSessionExpiry(null);
        toast.error('Session expired. Please verify again.');
      } else {
        toast.error(detail);
      }
    } finally { setSaving(false); }
  };

  const handleReveal = (keyId) => {
    requireSession(async () => {
      setRevealing(prev => ({ ...prev, [keyId]: true }));
      try {
        const res = await api.post(`/admin/api-keys/${keyId}/reveal`, {
          session_token: sessionToken,
        });
        setRevealedKeys(prev => ({ ...prev, [keyId]: res.data.fields }));
      } catch (e) {
        const detail = e.response?.data?.detail || 'Failed to reveal';
        if (detail.includes('session')) {
          setSessionToken('');
          setSessionExpiry(null);
        }
        toast.error(detail);
      } finally { setRevealing(prev => ({ ...prev, [keyId]: false })); }
    });
  };

  const handleHide = (keyId) => {
    setRevealedKeys(prev => {
      const next = { ...prev };
      delete next[keyId];
      return next;
    });
  };

  const handleDelete = (key) => {
    requireSession(() => setDeleteTarget(key));
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !isSessionActive) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/api-keys/${deleteTarget.id}`, {
        data: { session_token: sessionToken },
      });
      toast.success(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
      fetchKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(false); }
  };

  const getServiceCategory = (serviceId) => {
    return categories.find(c => c.id === serviceId) || { id: serviceId, name: serviceId, fields: ['api_key'] };
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const runHealthCheck = async () => {
    setHealthChecking(true);
    setHealthResults([]);
    try {
      const res = await api.post('/admin/api-keys/health-check');
      setHealthResults(res.data.results || []);
      setHealthCheckedAt(res.data.checked_at);
      const statuses = (res.data.results || []).map(r => r.status);
      const errors = statuses.filter(s => s === 'error').length;
      const warnings = statuses.filter(s => s === 'warning').length;
      const healthy = statuses.filter(s => s === 'healthy').length;
      if (errors > 0) toast.error(`Health check: ${errors} service(s) failing`);
      else if (warnings > 0) toast.warning(`Health check: ${warnings} warning(s)`);
      else if (healthy > 0) toast.success(`All ${healthy} configured service(s) healthy`);
      else toast.info('No services configured to check');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Health check failed');
    } finally { setHealthChecking(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-api-keys-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>API Key Management</h1>
          <p className="text-gray-500 text-sm mt-1">Securely manage third-party API keys with AES-256 encryption</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Session indicator */}
          {isSessionActive ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700" data-testid="session-active-badge">
              <CheckCircle className="w-3.5 h-3.5" />
              <span>Session active</span>
              <span className="font-mono font-medium">{formatTime(sessionRemaining)}</span>
            </div>
          ) : (
            <button
              onClick={() => { setPendingAction(null); setShowVerifyModal(true); setVerifyPassword(''); setVerifyCode(''); }}
              className="flex items-center gap-2 px-3 py-2 border-2 border-orange-200 text-orange-700 rounded-lg text-sm hover:bg-orange-50"
              data-testid="verify-session-btn"
            >
              <Lock className="w-4 h-4" /> Verify Identity
            </button>
          )}
          <button
            onClick={runHealthCheck}
            disabled={healthChecking}
            className="flex items-center gap-2 px-4 py-2 border-2 border-emerald-200 text-emerald-700 rounded-lg text-sm hover:bg-emerald-50 disabled:opacity-50"
            data-testid="health-check-btn"
          >
            {healthChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
            {healthChecking ? 'Checking...' : 'Health Check'}
          </button>
          <button
            onClick={() => requireSession(openAddModal)}
            className="flex items-center gap-2 px-4 py-2 bg-[#1A73E8] text-white rounded-lg text-sm hover:bg-[#1557B0]"
            data-testid="add-api-key-btn"
          >
            <Plus className="w-4 h-4" /> Add Key
          </button>
        </div>
      </div>

      {/* Security notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-amber-800 font-medium">Unified Key Source</p>
          <p className="text-xs text-amber-600 mt-0.5">
            Keys stored here are the primary source for all features (SMTP, Stripe, OpenAI, etc.). 
            If a key isn't stored here, the system falls back to Settings pages or .env file.
          </p>
        </div>
      </div>

      {/* Active Key Sources */}
      {sources.length > 0 && !loading && (
        <div className="bg-white rounded-xl border p-4" data-testid="key-sources-panel">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Active Key Sources</p>
          <div className="flex flex-wrap gap-3">
            {sources.map(s => {
              const sourceConfig = {
                vault: { label: 'Vault', cls: 'bg-green-100 text-green-700 border-green-200' },
                settings: { label: 'Settings', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
                env: { label: '.env', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
                not_configured: { label: 'Not Set', cls: 'bg-red-50 text-red-400 border-red-200' },
              };
              const cfg = sourceConfig[s.source] || sourceConfig.not_configured;
              return (
                <div key={s.service} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${cfg.cls}`} data-testid={`source-${s.service}`}>
                  <span>{SERVICE_ICONS[s.service] || '⚙️'}</span>
                  <span className="font-medium">{s.name}</span>
                  <span className="opacity-70">{cfg.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Health Check Results */}
      {healthResults.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden" data-testid="health-check-results">
          <div className="px-5 py-3 bg-gray-50 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Health Check Results</span>
            </div>
            {healthCheckedAt && (
              <span className="text-xs text-gray-400">
                Checked {new Date(healthCheckedAt).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
              </span>
            )}
          </div>
          <div className="divide-y">
            {healthResults.map(r => {
              const statusConfig = {
                healthy: { icon: CheckCircle, cls: 'text-green-600', bg: 'bg-green-50', label: 'Healthy' },
                warning: { icon: AlertCircle, cls: 'text-amber-600', bg: 'bg-amber-50', label: 'Warning' },
                error: { icon: XCircle, cls: 'text-red-600', bg: 'bg-red-50', label: 'Error' },
                not_configured: { icon: AlertCircle, cls: 'text-gray-400', bg: 'bg-gray-50', label: 'Not Configured' },
              };
              const cfg = statusConfig[r.status] || statusConfig.not_configured;
              const StatusIcon = cfg.icon;
              return (
                <div key={r.service} className={`px-5 py-3 flex items-center gap-4 ${cfg.bg}`} data-testid={`health-${r.service}`}>
                  <span className="text-lg w-6 text-center">{SERVICE_ICONS[r.service] || '⚙️'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">{r.name}</p>
                    <p className="text-xs text-gray-500 truncate">{r.message}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.source && r.source !== 'none' && r.source !== 'unknown' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border text-gray-500">{r.source}</span>
                    )}
                    <span className={`flex items-center gap-1 text-xs font-medium ${cfg.cls}`}>
                      <StatusIcon className="w-4 h-4" /> {cfg.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Keys Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#1A73E8]" /></div>
      ) : keys.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Key className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No API keys stored yet</p>
          <p className="text-xs text-gray-400 mt-1">Click "Add Key" to store your first encrypted API key</p>
        </div>
      ) : (
        <div className="space-y-4">
          {keys.map(key => {
            const isRevealed = !!revealedKeys[key.id];
            const displayFields = isRevealed ? revealedKeys[key.id] : key.fields;
            return (
              <div key={key.id} className="bg-white rounded-xl border p-5" data-testid={`api-key-card-${key.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{SERVICE_ICONS[key.service] || '⚙️'}</span>
                    <div>
                      <h3 className="font-medium text-gray-900 text-sm">{key.name}</h3>
                      <p className="text-xs text-gray-400">{getServiceCategory(key.service).name} &middot; Updated {new Date(key.updated_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => isRevealed ? handleHide(key.id) : handleReveal(key.id)}
                      disabled={revealing[key.id]}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                        isRevealed
                          ? 'border-green-200 text-green-700 bg-green-50 hover:bg-green-100'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                      data-testid={`reveal-key-${key.id}`}
                    >
                      {revealing[key.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                        isRevealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      {isRevealed ? 'Hide' : 'Reveal'}
                    </button>
                    <button
                      onClick={() => openEditModal(key)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-gray-200 text-gray-600 hover:bg-gray-50"
                      data-testid={`edit-key-${key.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(key)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`delete-key-${key.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
                {/* Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(displayFields || {}).map(([fname, fval]) => (
                    <div key={fname} className="bg-gray-50 rounded-lg px-3 py-2">
                      <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-0.5">{fname.replace(/_/g, ' ')}</p>
                      <p className={`text-sm font-mono ${isRevealed ? 'text-gray-900 select-all' : 'text-gray-500'}`} data-testid={`field-${key.id}-${fname}`}>
                        {fval || <span className="text-gray-300 italic">Not set</span>}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Verify Identity Modal */}
      {showVerifyModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowVerifyModal(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()} data-testid="verify-session-modal">
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Lock className="w-5 h-5" />
                  <span className="font-semibold text-sm uppercase tracking-wide">Identity Verification</span>
                </div>
                <button onClick={() => setShowVerifyModal(false)} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-sm text-gray-600">
                To access API keys, verify your identity. This grants a <strong>10-minute secure session</strong>.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Password</label>
                <input
                  type="password" value={verifyPassword}
                  onChange={e => setVerifyPassword(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none"
                  placeholder="Enter your admin password"
                  data-testid="verify-password-input"
                  onKeyDown={e => e.key === 'Enter' && handleVerifySession()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Authenticator Code <span className="text-gray-400 font-normal">(if 2FA enabled)</span></label>
                <input
                  value={verifyCode}
                  onChange={e => setVerifyCode(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 text-sm text-center tracking-widest font-mono focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none"
                  placeholder="000000"
                  maxLength={12}
                  data-testid="verify-code-input"
                  onKeyDown={e => e.key === 'Enter' && handleVerifySession()}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowVerifyModal(false)} className="flex-1 px-4 py-2.5 border rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
                <button
                  onClick={handleVerifySession}
                  disabled={verifying || !verifyPassword}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg text-sm font-medium hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-verify-btn"
                >
                  {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                  {verifying ? 'Verifying...' : 'Verify'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Key Modal */}
      {showKeyModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowKeyModal(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="key-form-modal">
            <div className="bg-[#202124] px-6 py-4 sticky top-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Key className="w-5 h-5" />
                  <span className="font-semibold text-sm">{editingKey ? 'Edit API Key' : 'Add New API Key'}</span>
                </div>
                <button onClick={() => setShowKeyModal(false)} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key Name</label>
                <input
                  value={keyForm.name}
                  onChange={e => setKeyForm({ ...keyForm, name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2.5 text-sm"
                  placeholder="e.g. Production Stripe Key"
                  data-testid="key-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Category</label>
                <select
                  value={keyForm.service}
                  onChange={e => {
                    const svc = e.target.value;
                    const cat = categories.find(c => c.id === svc);
                    const newFields = {};
                    if (cat) cat.fields.forEach(f => { newFields[f] = keyForm.fields[f] || ''; });
                    setKeyForm({ ...keyForm, service: svc, fields: newFields });
                  }}
                  className="w-full border rounded-lg px-3 py-2.5 text-sm"
                  data-testid="key-service-select"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Key Fields</label>
                {(getServiceCategory(keyForm.service).fields || []).map(fname => (
                  <div key={fname}>
                    <label className="block text-xs text-gray-500 mb-1 capitalize">{fname.replace(/_/g, ' ')}</label>
                    <input
                      value={keyForm.fields[fname] || ''}
                      onChange={e => setKeyForm({ ...keyForm, fields: { ...keyForm.fields, [fname]: e.target.value } })}
                      className="w-full border rounded-lg px-3 py-2.5 text-sm font-mono"
                      placeholder={editingKey ? '(leave as-is to keep current value)' : `Enter ${fname.replace(/_/g, ' ')}`}
                      data-testid={`key-field-${fname}`}
                    />
                  </div>
                ))}
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowKeyModal(false)} className="flex-1 px-4 py-2.5 border rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
                <button
                  onClick={handleSaveKey}
                  disabled={saving || !keyForm.name.trim()}
                  className="flex-1 px-4 py-2.5 bg-[#1A73E8] text-white rounded-lg text-sm font-medium hover:bg-[#1557B0] disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="save-key-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                  {saving ? 'Saving...' : editingKey ? 'Update Key' : 'Save Key'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setDeleteTarget(null)}>
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden" onClick={e => e.stopPropagation()} data-testid="delete-key-modal">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
              <div className="flex items-center gap-2 text-white">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-semibold text-sm uppercase tracking-wide">Delete API Key</span>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700">
                Are you sure you want to permanently delete <strong>"{deleteTarget.name}"</strong>? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2.5 border rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
                <button
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-delete-key-btn"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApiKeysPage;
